import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform, useAnimationControls } from "framer-motion";
import axios from "axios";
import {
  Heart,
  X as CloseIcon,
  ArrowRight,
  RotateCcw,
  Sparkles,
  Home,
} from "lucide-react";
import { Logo } from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SIGNUP_URL = "https://channelpartner.network/signup";

const DECISION_EVERY = 10; // ask every 10 swipes

// helper: return Hinglish text if present, fallback to English so the deck
// never shows blank even if a new row hasn't been translated yet.
const pick = (hi, en) => (hi && hi.trim()) ? hi : (en || "");

function buildSignupUrl({ name, likes, dislikes, total, source }) {
  const p = new URLSearchParams();
  p.set("source", source || "problems_swipe");
  if (name) p.set("name", name);
  p.set("likes", String(likes));
  p.set("skips", String(dislikes));
  p.set("swipes", String(total));
  return `${SIGNUP_URL}?${p.toString()}`;
}

export default function ProblemsSwipe() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [started, setStarted] = useState(false);
  const [painPoints, setPainPoints] = useState([]);
  const [idx, setIdx] = useState(0); // pointer to current top card
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [lastSwipeDir, setLastSwipeDir] = useState(null); // "like" | "skip" | null — drives exit animation direction
  const [tutorialActive, setTutorialActive] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load pain points
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pain-points`);
        if (!alive) return;
        setPainPoints(data);
      } catch (e) {
        setError("Couldn't load the problems. Please refresh.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const startSession = async () => {
    try {
      const { data } = await axios.post(`${API}/swipe-session`, {
        name: name || null,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
      setSessionId(data.id);
    } catch (e) {
      // Non-blocking - deck still works
      console.warn("session create failed", e?.message);
    }
    setStarted(true);
    // Show one-time swipe tutorial the first ever time
    try {
      if (!localStorage.getItem("cpn_swipe_tutorial_seen")) {
        setTutorialActive(true);
      }
    } catch (_) {
      // localStorage may be blocked; skip tutorial silently
    }
  };

  const dismissTutorial = () => {
    if (!tutorialActive) return;
    setTutorialActive(false);
    try {
      localStorage.setItem("cpn_swipe_tutorial_seen", "1");
    } catch (_) {
      // ignore
    }
  };

  const totalDone = likes + dislikes;
  const current = painPoints[idx];
  const finished = idx >= painPoints.length;

  // Auto-redirect after all 100
  useEffect(() => {
    if (started && finished && !decisionOpen) {
      const url = buildSignupUrl({
        name,
        likes,
        dislikes,
        total: totalDone,
        source: "problems_finished",
      });
      // Complete session
      if (sessionId) {
        axios
          .put(`${API}/swipe-session/${sessionId}`, {
            join_intent: "auto",
            reached_step: totalDone,
          })
          .catch(() => {});
      }
      const t = setTimeout(() => {
        window.location.href = url;
      }, 1600);
      return () => clearTimeout(t);
    }
  }, [started, finished, decisionOpen, name, likes, dislikes, sessionId, totalDone]);

  // Fire decision gate every N swipes
  const maybeOpenDecision = (newTotal) => {
    if (newTotal > 0 && newTotal % DECISION_EVERY === 0 && newTotal < painPoints.length) {
      setDecisionOpen(true);
    }
  };

  const recordSwipe = async (direction) => {
    if (!current) return;
    // Any swipe kills the tutorial immediately
    if (tutorialActive) dismissTutorial();
    // Set exit direction FIRST so AnimatePresence knows which way to fly
    setLastSwipeDir(direction);
    // Update local counters
    if (direction === "like") setLikes((n) => n + 1);
    else setDislikes((n) => n + 1);
    const newTotal = totalDone + 1;
    setIdx((i) => i + 1);
    // Fire-and-forget
    if (sessionId) {
      axios
        .post(`${API}/swipe`, {
          session_id: sessionId,
          pain_point_id: current.id,
          pain_point_number: current.number,
          direction,
        })
        .catch(() => {});
    }
    // Decision gate
    setTimeout(() => maybeOpenDecision(newTotal), 320);
  };

  const handleDecisionJoin = async () => {
    if (sessionId) {
      try {
        await axios.put(`${API}/swipe-session/${sessionId}`, {
          join_intent: "yes",
          reached_step: totalDone,
        });
      } catch (e) {
        // ignore
      }
    }
    const url = buildSignupUrl({
      name,
      likes,
      dislikes,
      total: totalDone,
      source: "problems_swipe",
    });
    window.location.href = url;
  };

  const handleDecisionContinue = async () => {
    if (sessionId) {
      axios
        .put(`${API}/swipe-session/${sessionId}`, {
          join_intent: "no",
          reached_step: totalDone,
        })
        .catch(() => {});
    }
    setDecisionOpen(false);
  };

  const restart = () => {
    setIdx(0);
    setLikes(0);
    setDislikes(0);
    setDecisionOpen(false);
    setStarted(false);
    setSessionId(null);
    setLastSwipeDir(null);
    setTutorialActive(false);
  };

  return (
    <div className="min-h-[100dvh]">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/40">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="home-btn"
              className="p-2 -ml-2 rounded-full text-[#64748B] hover:text-[#0F172A] hover:bg-white/60 transition-colors"
              aria-label="Home"
            >
              <Home className="w-5 h-5" />
            </button>
            <Logo className="h-7" />
          </div>
          <Counter likes={likes} dislikes={dislikes} total={painPoints.length} done={totalDone} />
        </div>
        {started && !finished && (
          <div className="max-w-3xl mx-auto px-5 pb-2">
            <div className="h-1 rounded-full bg-white/60 overflow-hidden">
              <div
                className="h-full bg-[#FF5E1A] transition-all duration-500"
                style={{ width: `${(totalDone / (painPoints.length || 1)) * 100}%` }}
                data-testid="swipe-progress-fill"
              />
            </div>
          </div>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-5 pt-28 pb-10">
        {loading && <SkeletonCard />}
        {!loading && error && (
          <div className="glass-strong rounded-3xl p-6 text-center text-[#475569]">
            {error}
          </div>
        )}

        {!loading && !error && !started && (
          <IntroPanel
            name={name}
            setName={setName}
            onStart={startSession}
            total={painPoints.length}
          />
        )}

        {!loading && !error && started && !finished && !decisionOpen && current && (
          <CardDeck
            painPoints={painPoints}
            idx={idx}
            onSwipe={recordSwipe}
            lastSwipeDir={lastSwipeDir}
            tutorialActive={tutorialActive}
            onTutorialEnd={dismissTutorial}
          />
        )}

        {!loading && !error && started && !finished && decisionOpen && (
          <DecisionGate
            likes={likes}
            dislikes={dislikes}
            totalDone={totalDone}
            painPointsCount={painPoints.length}
            name={name}
            onJoin={handleDecisionJoin}
            onContinue={handleDecisionContinue}
          />
        )}

        {!loading && !error && started && finished && (
          <FinishedPanel
            likes={likes}
            dislikes={dislikes}
            total={painPoints.length}
            onRestart={restart}
            onJoin={handleDecisionJoin}
          />
        )}
      </div>
    </div>
  );
}

// ============ Sub-components ============
const Counter = ({ likes, dislikes, total, done }) => (
  <div className="flex items-center gap-2 text-xs font-semibold">
    <span
      className="inline-flex items-center gap-1 rounded-full bg-[#FFF3ED] text-[#FF5E1A] px-2.5 py-1"
      data-testid="counter-likes"
    >
      <Heart className="w-3.5 h-3.5 fill-current" /> {likes}
    </span>
    <span
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 px-2.5 py-1"
      data-testid="counter-dislikes"
    >
      <CloseIcon className="w-3.5 h-3.5" /> {dislikes}
    </span>
    <span className="hidden sm:inline text-slate-400 text-[11px]" data-testid="counter-progress">
      {done}/{total}
    </span>
  </div>
);

const IntroPanel = ({ name, setName, onStart, total }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-strong rounded-3xl p-6 md:p-8"
  >
    <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5E1A] px-3 py-1.5 text-xs font-bold">
      <Sparkles className="w-3.5 h-3.5" />
      SWIPE DECK
    </div>
    <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0F172A] mt-4 leading-tight">
      100 problems CPs ke, 100 solution jo humne solve kiye channelpartner.network pe.
    </h1>
    <p className="text-[15px] text-[#475569] leading-relaxed mt-3">
      Har card mein ek real broker frustration hai aur{" "}
      <span className="font-semibold text-[#FF5E1A]">
        hum usko kaise solve karte hain
      </span>
      .<br />
      Solution pasand aaya? Swipe{" "}
      <span className="font-semibold text-[#FF5E1A]">right</span>. Nahi
      match hua? Swipe{" "}
      <span className="font-semibold text-slate-500">left</span>.
    </p>

    <div className="mt-6 flex flex-col gap-2">
      <label className="text-sm font-semibold text-[#0F172A]">
        Aapka naam (optional)
      </label>
      <input
        data-testid="swipe-name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Aapka naam"
        className="w-full px-4 py-4 rounded-2xl glass-input focus:outline-none focus:border-[#FF5E1A] focus:ring-2 focus:ring-[#FF5E1A]/20 text-base text-[#0F172A] placeholder:text-slate-500 transition-all"
      />
    </div>
    <button
      type="button"
      onClick={onStart}
      data-testid="swipe-start-btn"
      className="mt-6 w-full rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white py-4 text-lg font-semibold cp-shadow-orange inline-flex items-center justify-center gap-2"
    >
      View 100 Solutions <ArrowRight className="w-5 h-5" />
    </button>
  </motion.div>
);

const SkeletonCard = () => (
  <div className="glass-strong rounded-3xl p-8 animate-pulse">
    <div className="h-4 w-24 bg-slate-200 rounded mb-4" />
    <div className="h-8 w-3/4 bg-slate-200 rounded mb-3" />
    <div className="h-4 w-1/2 bg-slate-200 rounded" />
  </div>
);

const CardDeck = ({ painPoints, idx, onSwipe, lastSwipeDir, tutorialActive, onTutorialEnd }) => {
  // Show up to 3 stacked
  const visible = painPoints.slice(idx, idx + 3);
  return (
    <div
      className="relative w-full"
      style={{
        // Card area = viewport height minus top bar (~112px) and bottom pad (~110px for buttons+safe area)
        height: "min(680px, calc(100dvh - 220px))",
        minHeight: "440px",
      }}
    >
      <AnimatePresence initial={false} custom={lastSwipeDir}>
        {visible
          .slice()
          .reverse()
          .map((pp, i) => {
            const stackIdx = visible.length - 1 - i; // 0 = top
            const isTop = stackIdx === 0;
            return (
              <SwipeCard
                key={pp.id}
                pp={pp}
                stackIdx={stackIdx}
                isTop={isTop}
                onSwipe={isTop ? onSwipe : undefined}
                runTutorial={isTop && tutorialActive}
                onTutorialInteract={onTutorialEnd}
              />
            );
          })}
      </AnimatePresence>

      {/* Action buttons — pinned INSIDE the deck area, near the bottom of the card */}
      <div className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-6 pointer-events-none z-40">
        <button
          type="button"
          onClick={() => onSwipe("skip")}
          data-testid="btn-skip"
          className="pointer-events-auto w-14 h-14 rounded-full bg-white shadow-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:border-slate-300 hover:scale-105 active:scale-95 transition-all"
          aria-label="Skip"
        >
          <CloseIcon className="w-6 h-6" strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={() => onSwipe("like")}
          data-testid="btn-like"
          className="pointer-events-auto w-16 h-16 rounded-full bg-[#FF5E1A] shadow-xl shadow-orange-500/40 flex items-center justify-center text-white hover:bg-[#E04F00] hover:scale-105 active:scale-95 transition-all"
          aria-label="Like"
        >
          <Heart className="w-7 h-7 fill-current" />
        </button>
      </div>
    </div>
  );
};

const SwipeCard = ({ pp, stackIdx, isTop, onSwipe, view, runTutorial, onTutorialInteract }) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-14, 14]);
  const likeOpacity = useTransform(x, [30, 140], [0, 1]);
  const skipOpacity = useTransform(x, [-140, -30], [1, 0]);
  const controls = useAnimationControls();
  const interactedRef = React.useRef(false);

  const scale = 1 - stackIdx * 0.04;
  const y = stackIdx * 10;
  const zIndex = 30 - stackIdx;

  const handleDragStart = () => {
    interactedRef.current = true;
    if (runTutorial && onTutorialInteract) onTutorialInteract();
  };

  const handleDragEnd = (_e, info) => {
    if (!isTop || !onSwipe) return;
    if (info.offset.x > 120 || info.velocity.x > 800) {
      onSwipe("like");
    } else if (info.offset.x < -120 || info.velocity.x < -800) {
      onSwipe("skip");
    }
  };

  // One-time swipe tutorial: subtle tilt left → center → right → center.
  // Any drag interrupts (via interactedRef). Uses controls to drive `x` so
  // the existing LIKE / SKIP stamps naturally fade in on either side.
  useEffect(() => {
    if (!isTop || !runTutorial) return;
    let cancelled = false;
    const wait = (ms) =>
      new Promise((resolve) => setTimeout(resolve, ms));
    const run = async () => {
      await wait(400);
      if (cancelled || interactedRef.current) return;
      await controls.start({ x: -70, transition: { type: "spring", stiffness: 220, damping: 18 } });
      if (cancelled || interactedRef.current) return;
      await wait(300);
      if (cancelled || interactedRef.current) return;
      await controls.start({ x: 0, transition: { type: "spring", stiffness: 260, damping: 22 } });
      if (cancelled || interactedRef.current) return;
      await wait(150);
      if (cancelled || interactedRef.current) return;
      await controls.start({ x: 70, transition: { type: "spring", stiffness: 220, damping: 18 } });
      if (cancelled || interactedRef.current) return;
      await wait(300);
      if (cancelled || interactedRef.current) return;
      await controls.start({ x: 0, transition: { type: "spring", stiffness: 260, damping: 22 } });
      if (cancelled) return;
      if (onTutorialInteract) onTutorialInteract();
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isTop, runTutorial]);

  return (
    <motion.div
      layout
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={isTop ? { x, rotate, zIndex } : { zIndex }}
      initial={{ scale: scale - 0.05, y: y + 20, opacity: 0 }}
      animate={isTop ? controls : { scale, y, opacity: 1 }}
      exit={(direction) => ({
        x: direction === "like" ? 500 : direction === "skip" ? -500 : 0,
        opacity: 0,
        rotate: direction === "like" ? 30 : direction === "skip" ? -30 : 0,
        transition: { duration: 0.32 },
      })}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
      className={`absolute inset-0 rounded-3xl overflow-hidden select-none ${
        isTop ? "cursor-grab active:cursor-grabbing" : "pointer-events-none"
      }`}
      data-testid={isTop ? "top-card" : undefined}
      data-pp-number={pp.number}
    >
      {/* LIKE / SKIP stamps — top layer, above card body */}
      {isTop && (
        <>
          <motion.div
            style={{ opacity: likeOpacity }}
            className="absolute top-6 left-6 z-20 rotate-[-12deg] border-4 border-white bg-[#FF5E1A] text-white px-4 py-1.5 rounded-xl font-heading font-black text-2xl tracking-wider shadow-lg"
          >
            LIKE
          </motion.div>
          <motion.div
            style={{ opacity: skipOpacity }}
            className="absolute top-6 right-6 z-20 rotate-[12deg] border-4 border-white bg-slate-700 text-white px-4 py-1.5 rounded-xl font-heading font-black text-2xl tracking-wider shadow-lg"
          >
            SKIP
          </motion.div>
        </>
      )}

      <PanelCardBody pp={pp} />
    </motion.div>
  );
};

// ============ Card body ============
const PanelCardBody = ({ pp, compact = false }) => (
  <div
    className={`w-full h-full glass-strong flex flex-col justify-between ${
      compact ? "p-4" : "p-6 md:p-8 rounded-3xl"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-[#2A5CB8] bg-[#EAF2FF] px-2.5 py-1 rounded-full">
        {pp.category}
      </span>
      <span className="text-[11px] font-semibold text-slate-400">
        #{pp.number} / 100
      </span>
    </div>

    <div className={`${compact ? "mt-3" : "mt-5"} flex-1 flex flex-col gap-4`}>
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
          THE PAIN
        </p>
        <h2
          className={`font-heading font-bold text-[#0F172A] leading-[1.15] mt-1.5 ${
            compact ? "text-lg" : "text-[22px] md:text-[26px]"
          }`}
        >
          {pick(pp.frustration_hi, pp.frustration)}
        </h2>
      </div>

      <div>
        <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-400">
          YOUR FEELING
        </p>
        <p
          className={`italic text-slate-500 leading-relaxed mt-0.5 ${
            compact ? "text-[12px]" : "text-[13px]"
          }`}
        >
          &ldquo;{pick(pp.feeling_hi, pp.feeling)}&rdquo;
        </p>
      </div>

      <div
        className={`rounded-2xl text-white relative overflow-hidden border border-white/10 ${
          compact ? "p-3" : "p-5"
        }`}
        style={{
          background:
            "linear-gradient(135deg, #0F1E3D 0%, #17305F 55%, #0B1832 100%)",
          boxShadow:
            "0 14px 28px -14px rgba(15, 30, 61, 0.55), 0 1px 0 rgba(255,255,255,0.08) inset",
        }}
      >
        <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full bg-[#FF5E1A]/25 blur-3xl pointer-events-none" />
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#FFB088] relative">
          THE SOLUTION ON CHANNELPARTNER.NETWORK
        </p>
        <p
          className={`font-semibold text-white leading-snug mt-1.5 relative ${
            compact ? "text-sm" : "text-[15px] md:text-base"
          }`}
        >
          {pick(pp.solution_hi, pp.solution) || "(Solution coming soon)"}
        </p>
      </div>
    </div>

    {!compact && (
      <div className="mt-4 text-center text-[11px] text-slate-500">
        Solution pasand? Swipe → &nbsp; · &nbsp; ← nahi jama
      </div>
    )}
    {/* Space reserved for the floating swipe buttons */}
    {!compact && <div className="h-14 shrink-0" aria-hidden="true" />}
  </div>
);

const DecisionGate = ({
  likes,
  dislikes,
  totalDone,
  painPointsCount,
  name,
  onJoin,
  onContinue,
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ type: "spring", stiffness: 240, damping: 22 }}
    className="rounded-3xl p-6 md:p-8 text-white relative overflow-hidden"
    style={{
      background:
        "linear-gradient(135deg, #FF5E1A 0%, #E04F00 55%, #B83A00 100%)",
      boxShadow:
        "0 24px 48px -18px rgba(255, 94, 26, 0.5), 0 2px 0 rgba(255,255,255,0.2) inset",
    }}
    data-testid="decision-gate"
  >
    <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-white/12 blur-3xl pointer-events-none" />
    <div className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 px-3 py-1.5 text-xs font-semibold relative">
      <Sparkles className="w-3.5 h-3.5" />
      Quick check-in
    </div>
    <h2 className="font-heading text-2xl md:text-3xl font-bold mt-4 leading-tight relative">
      {name ? `${name}, aapne` : "Aapne"}{" "}
      <span className="underline decoration-white/60 underline-offset-4">
        {likes}
      </span>{" "}
      solutions ko like kiya out of {totalDone}.
    </h2>
    <p className="text-[15px] text-white/95 leading-relaxed mt-3 relative">
      {likes >= 5
        ? "Yeh clearly aapke problems hain. Ab imagine karo — yeh sab ek hi platform par solved."
        : likes >= 2
        ? "Kuch problems match ho rahe hain. Aage aur bhi hain jo shayad aapko relevant lagenge."
        : "Abhi tak match nahi mile? Aage 90+ problems aur hain — chances hain ki koi resonate karega."}
      {" "}
      Kya aap ChannelPartner.Network join karna chahenge?
    </p>
    <div className="mt-6 flex flex-col gap-3 relative">
      <button
        type="button"
        onClick={onJoin}
        data-testid="decision-join-btn"
        className="w-full rounded-2xl bg-white text-[#FF5E1A] hover:bg-white/95 py-4 text-lg font-bold inline-flex items-center justify-center gap-2 transition-all active:scale-[0.99]"
      >
        Yes, let&apos;s join while it&apos;s still free!
        <ArrowRight className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onContinue}
        data-testid="decision-continue-btn"
        className="w-full rounded-2xl bg-white/15 backdrop-blur-sm border border-white/40 text-white hover:bg-white/25 py-4 text-base font-semibold transition-all"
      >
        Not yet, show me more benefits
      </button>
    </div>
    <p className="mt-4 text-xs text-white/80 text-center relative">
      Progress saved · {painPointsCount - totalDone} problems left
    </p>
  </motion.div>
);

const FinishedPanel = ({ likes, dislikes, total, onRestart, onJoin }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-strong rounded-3xl p-6 md:p-8 text-center"
  >
    <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5E1A] px-3 py-1.5 text-xs font-bold">
      <Sparkles className="w-3.5 h-3.5" />
      Saare {total} khatam
    </div>
    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mt-4">
      Aapne {likes} solutions ko like kiya {total} mein se.
    </h2>
    <p className="text-[15px] text-[#475569] leading-relaxed mt-3">
      Aapko signup page par le ja rahe hain — waha se sab kuch use karna
      shuru kar sakte ho…
    </p>
    <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
      <button
        type="button"
        onClick={onJoin}
        data-testid="finished-signup-btn"
        className="rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-6 py-3.5 text-base font-semibold cp-shadow-orange inline-flex items-center justify-center gap-2"
      >
        Sign Up Now <ArrowRight className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onRestart}
        data-testid="finished-restart-btn"
        className="rounded-2xl bg-white border-2 border-slate-200 text-[#0F172A] px-6 py-3.5 text-base font-semibold hover:border-[#FF5E1A] hover:text-[#FF5E1A] transition-all inline-flex items-center justify-center gap-2"
      >
        <RotateCcw className="w-4 h-4" /> Dobara swipe karo
      </button>
    </div>
  </motion.div>
);
